import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiFetch } from "@/lib/sdk"

/**
 * Keel Home data (GET /admin/terify/dashboard/home, backend lib/home) and the signed-in staff
 * member's Home settings (GET/POST /admin/terify/me/home, kept on their user record and cached in
 * localStorage so the page opens on the right range at once). Metric definitions come with the
 * data (backend lib/metrics/definitions.ts), so the tooltips here say exactly what the numbers do.
 */
export type MetricKey =
  | "net_sales"
  | "orders"
  | "aov"
  | "sessions"
  | "conversion"
  | "contribution"
  | "new_customers"
  | "gross_sales"
  | "discounts"
  | "refunds"
  | "processor_fees"
  | "cogs"
  | "shipping_packaging"
  | "mrr"
  | "approval_rate"
  | "ad_spend"
  | "poas"
export type RangeKey = "today" | "yesterday" | "7d" | "30d" | "90d" | "mtd" | "custom"
export type CompareKey = "prior" | "same_day_last_week" | "none"
export type Format = "money" | "number" | "percent" | "ratio"
export type MetricDef = { key: MetricKey; label: string; short?: string; format: Format; good: "up" | "down"; definition: string; source: string; refresh: string; series: boolean }
export type Tone = "neutral" | "info" | "success" | "attention" | "warning" | "critical"
export type BadgeT = { label: string; tone: Tone; hint?: string }
/** Why a delta isn't shown (backend lib/metrics/definitions.ts compared()): the comparison is before the launch, partly before it, or zero / missing. */
export type NoBaseline = "pre_launch" | "partial" | "zero"
export type BaselineStatus = "ok" | "none" | "pre_launch" | "partial"
export type Launch = { date: string | null; source: "setting" | "first_order" | null; first_order_date: string | null; setting: string | null }
export type Kpi = { value: number | null; previous: number | null; delta: { abs: number | null; pct: number | null }; delta_pts: number | null; no_baseline: NoBaseline | null; spark: (number | null)[] }

export type MidHealth = {
  settled: { keel_net: number; other_net: number; keel_count: number; batches: { batch_id: string; settled_at: string; label: string; keel_net: number; other_net: number }[]; label: string | null } | null
  unsettled: { keel_net: number; other_net: number; count: number; authorized: number; label: string; oldest_at: string | null } | null
  approval: { rate: number | null; previous: number | null; delta_pts: number | null; no_baseline: NoBaseline | null; approved: number; declined: number }
  declines: { count: number; cost: number; previous: number | null }
  /** The same figure as "Where the money went" → Refunds: refunds + cancellations that gave money back. */
  refunds: { count: number; amount: number; refunds: number; cancellations: number }
  chargebacks: {
    month: string
    month_label: string
    count: number
    open: number
    settled_transactions: number
    ratio: number | null
    limit: number | null
    level: "unset" | "ok" | "alert" | "over"
    used: number | null
    account_wide: { count: number; transactions: number; ratio: number | null } | null
  }
}
export type AttentionItem = { key: string; count: number; title: string; detail: string; chip: string | null; tier: number; to: string; perm: string; money?: boolean; age_hours?: number | null; due_hours?: number | null; cover_days?: number | null }
export type SourceRow = { key: string; label: string; sessions: number; orders: number; net_sales: number; cvr: number | null; contribution: number; no_visit?: boolean }

/** "Ad campaigns" (backend lib/home/ad-campaigns.ts): platform spend, Meta-reported purchases and Keel's last-click orders per campaign. */
export type AdRow = {
  key: string
  level: "campaign" | "adset" | "ad"
  platform: string
  platform_label: string
  id: string | null
  name: string
  spend: number
  impressions: number
  clicks: number
  cpc: number | null
  meta_purchases: number | null
  meta_purchase_value: number | null
  meta_roas: number | null
  orders: number
  revenue: number
  roas: number | null
  matched_on_name: number
  children: AdRow[]
}
export type AdPlatformSync = { connected: boolean; last_sync_at: string | null; last_sync_ok: boolean | null; last_sync_error: string | null; spend_from: string | null }
export type AdCampaigns = {
  since: string
  until: string
  model: string
  window_days: number
  rows: AdRow[]
  total: Pick<AdRow, "spend" | "impressions" | "clicks" | "cpc" | "meta_purchases" | "meta_purchase_value" | "meta_roas" | "orders" | "revenue" | "roas">
  orders: { all: number; attributed: number; organic_social: number; unattributed: number }
  meta_visits: { total: number; tagged: number; fbclid_only: number }
  notice: { show: boolean; reason: "fbclid_only" | "no_tagged_visits" | null; url_parameters: string }
  platforms: Record<"meta" | "google", AdPlatformSync>
}

export type HomeData = {
  generated_at: string
  build_ms: number
  store: { id: string; name: string; timezone: string; currency_code: string }
  stores: { id: string; name: string }[]
  window: { range: RangeKey; label: string; since: string; until: string; grain: "hour" | "day"; compare: CompareKey; compare_since: string | null; compare_until: string | null; baseline: string | null; baseline_detail: string | null; baseline_short: string | null; same_hour: boolean; baseline_status: BaselineStatus; launch: Launch }
  definitions: Record<MetricKey, MetricDef>
  kpis: Record<MetricKey, Kpi>
  contribution_pct: { value: number | null; previous: number | null; delta_pts: number | null; no_baseline: NoBaseline | null }
  chart: { grain: "hour" | "day"; cumulative: boolean; keys: string[]; compare_keys: string[]; last_index: number; current: Partial<Record<MetricKey, (number | null)[]>>; previous: Partial<Record<MetricKey, (number | null)[]>> | null }
  funnel: { available: boolean; steps: { key: string; label: string; value: number; previous: number | null }[]; conversions: { from: string; to: string; rate: number | null }[]; weakest: string | null; declines: number }
  waterfall: {
    gross_sales: number
    discounts: number
    refunds: number
    refund_detail: { refunds: number; refunded: number; cancellations: number; cancelled: number }
    net_sales: number
    processor_fees: number
    cogs: number
    shipping_packaging: number
    chargeback_losses: number
    contribution: number
    contribution_pct: number | null
    shipping_charged: number
    fees: { order_fees: number; fees_given_back: number; refund_fees: number; chargeback_fees: number; card_checks: number; void_fees?: number; declines?: number; fixed: number; true_up: number }
    fee_source: { profiles: { id: string; name: string; default: boolean; percent: number | null; fixed: number | null; monthly_fixed: number | null; refund_fee: number | null; chargeback_fee: number | null }[]; default_profile: string; trued_up_months: string[]; months: string[] }
    warnings: { lines_missing_cost: number; net_sales_missing_cost: number; shipping_estimated_orders: number; shipping_estimated_amount: number; shipping_missing_orders: number }
  }
  payments: { mids: { id: string; name: string; default?: boolean; limit?: number | null }[]; by_mid: Record<string, MidHealth> }
  subscriptions: {
    definitions: Record<"active_subscriptions" | "subscription_customers", { label: string; definition: string }>
    active_subscribers: number
    subscribers_net_change: number
    active_subscriptions: number
    subscriptions_net_change: number
    new: number
    cancelled: number
    reactivated: number
    mrr: number
    mrr_previous: number | null
    mrr_delta: { abs: number | null; pct: number | null }
    mrr_no_baseline: NoBaseline | null
    renewal: { rate: number | null; attempted: number; paid: number; previous: number | null; delta_pts: number | null; no_baseline: NoBaseline | null }
    retrying: { count: number; amount: number; oldest_at: string | null; next_retry_at: string | null }
  } | null
  attention: AttentionItem[]
  ad_campaigns: AdCampaigns
  acquisition: { source: SourceRow[]; utm: SourceRow[]; total: Record<"source" | "utm", { sessions: number; orders: number; net_sales: number; contribution: number }>; store_wide_costs: number; model: "last_non_direct_7d" }
  recent_orders: { id: string; display_id: number; name: string; customer_name: string | null; email: string | null; total: number; currency_code: string; placed_at: string; kind: string; payment: BadgeT; fulfillment: BadgeT }[]
  sync: { settlements: { connected: boolean; last_ok_at: string | null; last_error: string | null }; ad_spend_at: string | null }
}

export type HomePrefs = { range: RangeKey; since: string | null; until: string | null; compare: CompareKey; tiles: MetricKey[]; chart: MetricKey; mid: string }
export const DEFAULT_TILES: MetricKey[] = ["net_sales", "orders", "aov", "sessions", "conversion", "contribution"]
export const DEFAULT_PREFS: HomePrefs = { range: "today", since: null, until: null, compare: "same_day_last_week", tiles: DEFAULT_TILES, chart: "net_sales", mid: "all" }
/** Metrics the "Edit tiles" picker offers, in this order. */
export const PICKABLE: MetricKey[] = ["net_sales", "orders", "aov", "sessions", "conversion", "contribution", "new_customers", "gross_sales", "discounts", "refunds", "processor_fees", "cogs", "shipping_packaging", "mrr", "approval_rate", "ad_spend", "poas"]

const LS_KEY = "keel.home.prefs"
export function cachedPrefs(): HomePrefs | null {
  try {
    const raw = localStorage.getItem(LS_KEY)
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : null
  } catch {
    return null
  }
}
const cachePrefs = (p: HomePrefs) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(p))
  } catch {}
}

export function useHomePrefs() {
  return useQuery({
    queryKey: ["home", "prefs"],
    queryFn: async () => {
      const r = await apiFetch<{ prefs: HomePrefs }>("/admin/terify/me/home")
      cachePrefs(r.prefs)
      return r.prefs
    },
    placeholderData: cachedPrefs() ?? undefined,
    staleTime: 5 * 60_000,
  })
}

export function useSaveHomePrefs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<HomePrefs>) => apiFetch<{ prefs: HomePrefs }>("/admin/terify/me/home", { method: "POST", body: JSON.stringify(patch) }).then((r) => r.prefs),
    onMutate: (patch) => {
      const cur = qc.getQueryData<HomePrefs>(["home", "prefs"]) ?? cachedPrefs() ?? DEFAULT_PREFS
      const next = { ...cur, ...patch }
      qc.setQueryData(["home", "prefs"], next)
      cachePrefs(next)
    },
    onSuccess: (p) => {
      qc.setQueryData(["home", "prefs"], p)
      cachePrefs(p)
    },
  })
}

/** Set after a change Home itself made (a chargeback limit): the next load skips the server's 30 s cache. */
let forceFresh = false
export type HomeSel = { range: RangeKey; since?: string | null; until?: string | null; compare: CompareKey }
export function useHome(sel: HomeSel | null, live: boolean) {
  const query: Record<string, string> = sel ? { range: sel.range, compare: sel.compare, ...(sel.range === "custom" && sel.since && sel.until ? { since: sel.since, until: sel.until } : {}) } : {}
  return useQuery({
    queryKey: ["home", "data", query],
    enabled: !!sel && (sel.range !== "custom" || !!(sel.since && sel.until)),
    queryFn: () => {
      const fresh = forceFresh
      forceFresh = false
      return apiFetch<HomeData>(`/admin/terify/dashboard/home?${new URLSearchParams({ ...query, ...(fresh ? { fresh: "1" } : {}) })}`)
    },
    placeholderData: keepPreviousData,
    // A range that includes today keeps itself current (the server shares results for 30 s).
    refetchInterval: live ? 60_000 : false,
    refetchIntervalInBackground: false,
  })
}

/** The store's launch day for Home's comparisons (backend lib/home/launch.ts): set by hand, or null for the first real order's day. */
export function useSaveLaunchDate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (launch_date: string | null) => apiFetch<{ launch: Launch }>("/admin/terify/dashboard/home/settings", { method: "POST", body: JSON.stringify({ launch_date }) }),
    onSuccess: () => {
      forceFresh = true
      return qc.invalidateQueries({ queryKey: ["home", "data"] })
    },
  })
}

/** Set by "Sync now": the next Ad campaigns load skips the server's 30 s cache. */
let adFresh = false
/** The same "Ad campaigns" table for any range (Analytics → Reports → Ad campaigns). */
export function useAdCampaigns(range: { since: string; until: string } | null) {
  return useQuery({
    queryKey: ["ad-campaigns", range],
    enabled: !!range,
    queryFn: () => {
      const fresh = adFresh
      adFresh = false
      return apiFetch<AdCampaigns>(`/admin/terify/analytics/ad-campaigns?${new URLSearchParams({ ...range!, ...(fresh ? { fresh: "1" } : {}) })}`)
    },
    placeholderData: keepPreviousData,
  })
}

/**
 * "Sync now" (analytics:write): pulls the last 3 days through today from every connected ad
 * platform (POST /admin/terify/analytics/ad-spend/sync, the same as Settings → Customer events →
 * Ad spend), then reloads Home and the Ad campaigns report without their 30 s cache.
 */
export function useSyncAdSpend() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (platforms: ("meta" | "google")[]) => {
      const out: { platform: string; ok: boolean; message?: string; rows?: number }[] = []
      for (const platform of platforms) {
        try {
          const r = await apiFetch<{ sync: { ok: boolean; message?: string; rows?: number } }>("/admin/terify/analytics/ad-spend/sync", { method: "POST", body: JSON.stringify({ platform }) })
          out.push({ platform, ...r.sync })
        } catch (e: any) {
          out.push({ platform, ok: false, message: e?.message ?? "Sync failed" })
        }
      }
      return out
    },
    onSettled: async () => {
      forceFresh = true
      adFresh = true
      await Promise.all([qc.invalidateQueries({ queryKey: ["home", "data"] }), qc.invalidateQueries({ queryKey: ["ad-campaigns"] })])
    },
  })
}

export function useSaveChargebackLimit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (b: { profile: string; chargeback_limit_percent: number | null }) => apiFetch("/admin/terify/profit/chargeback-limit", { method: "POST", body: JSON.stringify(b) }),
    onSuccess: () => {
      forceFresh = true
      return qc.invalidateQueries({ queryKey: ["home", "data"] })
    },
  })
}
